import React, { useRef, useState } from 'react';
import { useGallery } from '../../hooks/useGallery';

const F = "'Inter', system-ui, sans-serif";

export default function GalleryManager() {
  const { photos, loading, error, uploadPhoto, deletePhoto, reorderPhotos } = useGallery();
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const fileRef = useRef();

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadPhoto(file, caption);
      setCaption('');
      fileRef.current.value = '';
    } catch (err) {
      alert('Upload fehlgeschlagen: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm('Foto wirklich löschen?')) return;
    try {
      await deletePhoto(filename);
    } catch (err) {
      alert('Löschen fehlgeschlagen: ' + err.message);
    }
  };

  // Drag-to-reorder
  const handleDragStart = (i) => setDragIdx(i);
  const handleDragOver = (e, i) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    const reordered = [...photos];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(i, 0, moved);
    setDragIdx(i);
    reorderPhotos(reordered);
  };
  const handleDragEnd = () => setDragIdx(null);

  return (
    <div>
      {/* Upload */}
      <div style={styles.uploadBox}>
        <h3 style={styles.boxTitle}>Foto hochladen</h3>
        <div style={styles.uploadRow}>
          <input
            type="text"
            placeholder="Beschriftung (optional)"
            value={caption}
            onChange={e => setCaption(e.target.value)}
            style={styles.captionInput}
          />
          <label style={{ ...styles.uploadBtn, opacity: uploading ? 0.6 : 1 }}>
            {uploading ? 'Hochladen...' : '+ Foto auswählen'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              ref={fileRef}
              onChange={handleUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
        </div>
        <p style={styles.hint}>Erlaubte Formate: JPG, PNG, WebP. Max. 10 MB.</p>
      </div>

      {/* Grid */}
      <div style={styles.section}>
        <h3 style={styles.boxTitle}>
          Vorhandene Fotos
          <span style={styles.badge}>{photos.length}</span>
        </h3>
        <p style={styles.hint}>Reihenfolge durch Ziehen ändern. Klick auf ✕ löscht das Foto.</p>

        {loading && <p style={styles.info}>Lade Galerie...</p>}
        {error && <p style={styles.errorText}>Fehler: {error}</p>}

        <div style={styles.grid}>
          {photos.map((photo, i) => (
            <div
              key={photo.filename}
              draggable
              onDragStart={() => handleDragStart(i)}
              onDragOver={e => handleDragOver(e, i)}
              onDragEnd={handleDragEnd}
              style={{ ...styles.card, ...(dragIdx === i ? styles.cardDragging : {}) }}
            >
              <img src={photo.url} alt={photo.caption || ''} style={styles.img} />
              <div style={styles.cardOverlay}>
                {photo.caption && <span style={styles.caption}>{photo.caption}</span>}
                <button
                  style={styles.deleteBtn}
                  onClick={() => handleDelete(photo.filename)}
                  title="Foto löschen"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
          {!loading && photos.length === 0 && (
            <p style={styles.info}>Noch keine Fotos hochgeladen.</p>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  uploadBox: { backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' },
  section: { backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1.5rem' },
  boxTitle: { color: '#333', fontFamily: '' + F + '', fontSize: '1.1rem', fontWeight: '700', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #28a745', display: 'flex', alignItems: 'center', gap: '0.5rem' },
  badge: { backgroundColor: '#28a745', color: 'white', borderRadius: '20px', padding: '0.1rem 0.6rem', fontSize: '0.8rem', fontWeight: '600' },
  uploadRow: { display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' },
  captionInput: { flex: 1, minWidth: '180px', padding: '0.6rem 0.75rem', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.95rem', fontFamily: '' + F + '' },
  uploadBtn: { backgroundColor: '#28a745', color: 'white', padding: '0.6rem 1.2rem', borderRadius: '5px', cursor: 'pointer', fontFamily: '' + F + '', fontSize: '0.95rem', fontWeight: '600', whiteSpace: 'nowrap' },
  hint: { color: '#888', fontSize: '0.8rem', fontFamily: '' + F + '', marginTop: '0.5rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem', marginTop: '1rem' },
  card: { position: 'relative', borderRadius: '6px', overflow: 'hidden', aspectRatio: '4/3', backgroundColor: '#eee', cursor: 'grab', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' },
  cardDragging: { opacity: 0.5, boxShadow: '0 4px 15px rgba(40,167,69,0.4)' },
  img: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  cardOverlay: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', padding: '0.4rem', background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 60%)' },
  caption: { color: 'white', fontSize: '0.75rem', fontFamily: '' + F + '', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  deleteBtn: { background: 'rgba(220,53,69,0.9)', border: 'none', color: 'white', borderRadius: '4px', width: '24px', height: '24px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  info: { color: '#888', fontFamily: '' + F + '', fontSize: '0.9rem' },
  errorText: { color: '#dc3545', fontFamily: '' + F + '', fontSize: '0.9rem' },
};
